import { useEffect, useState } from 'react';
import { commerceApi, financeApi } from '../lib/api';

function money(minor: number, currency = 'USD') {
  return `${(minor / 100).toFixed(2)} ${currency}`;
}

/** Commerce et finance : produits, prix, grand livre, partages de revenus, wallet. */
export function FinanceAdminPage() {
  const [products, setProducts] = useState<any[]>([]);
  const [prices, setPrices] = useState<any[]>([]);
  const [ledger, setLedger] = useState<any[]>([]);
  const [splits, setSplits] = useState<any[]>([]);
  const [entitlements, setEntitlements] = useState<any[]>([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => { reload(); }, []);

  async function reload() {
    for (const [fn, set] of [
      [commerceApi.products, setProducts],
      [commerceApi.prices, setPrices],
      [commerceApi.entitlements, setEntitlements],
      [financeApi.ledger, setLedger],
      [financeApi.splits, setSplits],
    ] as any[]) {
      try { set(await fn()); } catch { /* droits insuffisants ou vide */ }
    }
  }

  async function run(fn: () => Promise<any>, note: string) {
    setError(''); setNotice('');
    try { await fn(); setNotice(note); await reload(); }
    catch (e: any) { setError(String(e.message || e)); }
  }

  const ask = (l: string, d = '') => window.prompt(l, d) ?? '';

  return <section className="page">
    <header className="page-header">
      <div>
        <span className="eyebrow">FINANCE</span>
        <h1>Commerce et grand livre</h1>
        <p>Produits, tarifs, partages de revenus et écritures comptables.</p>
      </div>
    </header>

    {error && <div className="alert error">{error}</div>}
    {notice && <div className="notice">{notice}</div>}

    <div className="grid two">
      <div className="card">
        <h3>Produits ({products.length})</h3>
        <button className="button secondary" onClick={() => {
          const code = ask('Code produit'); if (!code) return;
          const name = ask('Nom') || code;
          run(() => commerceApi.createProduct({ code, name }), 'Produit créé.');
        }}>Ajouter un produit</button>
        {products.map(p => <div key={p.id} className="order-row">
          <strong>{p.code} — {p.name}</strong>
          <button className="button secondary" onClick={() => {
            const raw = ask('Montant en centimes', '1000'); if (!raw) return;
            run(() => commerceApi.createPrice({ product_id: p.id, amount_minor: Number(raw), currency: 'USD', unit: 'ONE_TIME' }), 'Prix fixé.');
          }}>Fixer un prix</button>
        </div>)}
        {!products.length && <p className="technical-note">Aucun produit.</p>}
      </div>

      <div className="card">
        <h3>Partages de revenus ({splits.length})</h3>
        <p className="technical-note">Exprimés en points de base : 10000 = 100 %.</p>
        <button className="button secondary" onClick={() => {
          const scope = ask('Portée (ex. INSTITUTION)', 'INSTITUTION'); if (!scope) return;
          const platform = Number(ask('Part plateforme en bps', '2000')) || 0;
          run(() => financeApi.setSplit({ scope, platform_bps: platform, beneficiary_bps: 10000 - platform }), 'Partage enregistré.');
        }}>Définir un partage</button>
        {splits.map(s => <div key={s.id || s.scope} className="order-row">
          <strong>{s.scope}</strong>
          <span>plateforme {(s.platform_bps / 100).toFixed(1)} % · bénéficiaire {(s.beneficiary_bps / 100).toFixed(1)} %</span>
        </div>)}
        {!splits.length && <p className="technical-note">Aucun partage défini.</p>}
      </div>
    </div>

    <div className="grid two">
      <div className="card">
        <h3>Tarifs actifs ({prices.length})</h3>
        {prices.map(p => <div key={p.id} className="order-row">
          <strong>{(p.product_id || '').slice(0, 8)}</strong>
          <span>{money(p.amount_minor, p.currency)} · {p.unit}</span>
        </div>)}
        {!prices.length && <p className="technical-note">Aucun tarif.</p>}

        <h3>Créditer un wallet</h3>
        <button className="button secondary" onClick={() => {
          const userId = ask('Identifiant utilisateur'); if (!userId) return;
          const raw = ask('Montant en centimes', '1000'); if (!raw) return;
          run(() => financeApi.creditWallet(userId, { amount_minor: Number(raw), external_key: `manual-${Date.now()}` }), 'Wallet crédité.');
        }}>Créditer</button>
      </div>

      <div className="card">
        <h3>Grand livre ({ledger.length})</h3>
        {ledger.slice(0, 20).map((e, i) => <div key={e.id || i} className="order-row">
          <strong>{e.account}</strong>
          <span>{e.direction} {money(e.amount_minor, e.currency)}</span>
        </div>)}
        {!ledger.length && <p className="technical-note">Aucune écriture.</p>}

        <h3>Droits acquis ({entitlements.length})</h3>
        {entitlements.slice(0, 10).map((e, i) => <div key={e.id || i} className="order-row">
          <strong>{(e.product_id || e.reference_id || '').slice(0, 8)}</strong>
          <span>{e.status}</span>
        </div>)}
      </div>
    </div>
  </section>;
}
